<template>
    <section v-if="course" name="course view" class="gap-xs -m-xs bg-primary relative col-span-12 mb-16 flex h-full">
        <div
            class="bg-lightest relative rounded-sm"
            :class="{ 'w-0 p-0': collapseMenu, 'p-sm w-[99vw] lg:w-[65rem]': !collapseMenu }"
        >
            <CourseMenu
                v-show="!collapseMenu"
                :course="course"
                :completed-sections="completedSections"
                :active-section="activeSection!"
                @update-section="updateSection"
            />
            <button
                class="collapse-btn absolute transform transition-transform duration-300"
                :class="{ '-right-8 rotate-180': collapseMenu, '-right-2 lg:-right-5': !collapseMenu }"
                @click="toggleCollapseMenu"
            >
                <i class="i-double-arrow block" />
            </button>
        </div>
        <div
            :data-menu-collapse="collapseMenu"
            class="bg-lightest gap-md col-span-6 col-start-6 flex w-full flex-col rounded-sm data-[menu-collapse=false]:hidden lg:data-[menu-collapse=false]:flex"
        >
            <CourseSection
                :key="activeSection?.section_id"
                :section="activeSection!"
                :incorrect="incorrectQuiz"
                :completed-quiz="completedSections.includes(activeSection!)"
                @options-selected="handleAllOptionsSelected"
            />
            <div
                class="p-sm mb-sm mt-auto flex flex-wrap items-center justify-between gap-4 border-t border-(--border-subtle) lg:mb-0 lg:flex-nowrap"
            >
                <div class="flex gap-4">
                    <button
                        class="gap-sm font-matter flex items-center font-normal"
                        :disabled="completedSections.length < 1 || activeSection === course.sections[0]"
                        @click="prevSection()"
                    >
                        <i class="i-arrow-right rotate-180" />
                        <span> Previous </span>
                    </button>
                    <button
                        class="gap-sm font-matter flex items-center font-normal"
                        :disabled="
                            completedSections.length ===
                                course.sections.findIndex((section) => section === activeSection) ||
                            !completedSections.includes(activeSection!)
                        "
                        @click="nextSection()"
                    >
                        <span>Next</span>
                        <i class="i-arrow-right" />
                    </button>
                </div>
                <Button
                    v-if="!incorrectQuiz"
                    :disabled="forbidQuizSubmission && !(activeSection?.quizes.length === 0)"
                    class="btn w-full hover:bg-(--btn-primary-bg) lg:w-auto"
                    @:click="completeSection(activeSection!)"
                >
                    {{ activeSection === course.sections[course.sections.length - 1] ? 'Finish' : 'Complete' }}
                </Button>
                <Button v-else class="btn w-full lg:w-auto" @click="reloadPage">Retry</Button>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import CourseMenu from '@/components/lms/course-menu.vue';
import { onMounted, onUnmounted, ref, nextTick, inject } from 'vue';
import lmsRouter from '@/router/lms-router';
import { useLMSStore } from '@/stores/lms';
import CourseSection from '@/components/lms/course-section.vue';

import type { Course, QuestionOption, Section } from '@/stores/store-interfaces';
import { useIsDesktop } from '@/composables/isDesktop';

const lmsStore = useLMSStore();
const course = ref<Course>();
const isDesktop = useIsDesktop();
const collapseMenu = ref(!isDesktop.value); // to handle collapse, initial value depends on if mobile/desktop

function toggleCollapseMenu() {
    collapseMenu.value = !collapseMenu.value;
}
const _scrollToTop = inject<() => void>('scrollToTop');
function scrollToTop() {
    // TODO, if we need to use this on some other page -> make this cleaner, or move this into a composable
    if (_scrollToTop) {
        nextTick(() => {
            _scrollToTop();
        });
    }
}

const activeSection = ref<Section>(); // to decide on which section to auto expand
const completedSections = ref<Section[]>([]);
const forbidQuizSubmission = ref(true);
const incorrectQuiz = ref();
const selectedOptions = ref<QuestionOption[]>();

const props = defineProps({
    courseId: {
        type: String,
        required: true,
    },
});

onMounted(async () => {
    await setup();
});

onUnmounted(() => {
    lmsStore.setActiveSectionIndex('0');
});

async function setup() {
    course.value = await lmsStore.getCourse(props.courseId);
    activeSection.value = course.value?.sections[Number.parseInt(lmsStore.activeSectionIndex)];
    if (lmsStore.completedSectionLastIndex !== '0') {
        completedSections.value =
            course.value?.sections?.slice(0, Number.parseInt(lmsStore.completedSectionLastIndex) + 1) || [];
    }
}

async function completeSection(section: Section) {
    const sectionIndex = course.value?.sections.findIndex((s) => s === section);
    if (sectionIndex === undefined || sectionIndex < 0) {
        console.warn('Section not found.');
        return;
    }

    // quiz check logic
    if (section.quizes.length !== 0) {
        if (selectedOptions.value?.some((q: QuestionOption) => q.correct === false)) {
            incorrectQuiz.value = true;
            scrollToTop();
            return;
        }
    }

    completedSections.value.push(section);

    const nextSectionIndex = sectionIndex + 1;
    const courseSections = course.value?.sections;

    if (courseSections && nextSectionIndex < courseSections.length) {
        lmsStore.setCompletedSectionLastIndex(sectionIndex.toString());
        lmsStore.setActiveSectionIndex(nextSectionIndex.toString());
        activeSection.value = courseSections[nextSectionIndex];
        forbidQuizSubmission.value = true;
        scrollToTop();
    } else {
        await lmsStore.completeCourse(course.value!.course_id);
        lmsRouter.push({ name: 'completed', params: { courseId: course.value!.course_id } });
    }
}

function nextSection() {
    const currentSectionIndex = course.value?.sections.findIndex((section) => section === activeSection.value);
    if (currentSectionIndex === undefined || currentSectionIndex < 0) {
        console.warn('Current section not found.');
        return;
    }

    const nextSectionIndex = currentSectionIndex + 1;
    const courseSections = course.value?.sections;

    if (courseSections && nextSectionIndex < courseSections.length) {
        lmsStore.setActiveSectionIndex(nextSectionIndex.toString());
        activeSection.value = courseSections[nextSectionIndex];
        scrollToTop();
    } else {
        console.warn('No more sections available.');
    }
}

function prevSection() {
    const currentSectionIndex = course.value?.sections.findIndex((section) => section === activeSection.value);
    if (currentSectionIndex === undefined || currentSectionIndex < 0) {
        console.warn('Current section not found.');
        return;
    }

    const prevSectionIndex = currentSectionIndex - 1;
    const courseSections = course.value?.sections;

    if (courseSections && prevSectionIndex >= 0) {
        lmsStore.setActiveSectionIndex(prevSectionIndex.toString());
        activeSection.value = courseSections[prevSectionIndex];
        scrollToTop();
    } else {
        console.warn('No previous sections available.');
    }
}

function updateSection(section: Section) {
    const sectionIndex = course.value?.sections.findIndex((s) => s === section);
    if (
        completedSections.value.includes(section) ||
        (course.value &&
            sectionIndex ===
                course.value.sections.findIndex(
                    (s) => s === completedSections.value[completedSections.value.length - 1],
                ) +
                    1)
    ) {
        activeSection.value = section;
    }
}

function handleAllOptionsSelected(userSelectedOptions: QuestionOption[]) {
    selectedOptions.value = userSelectedOptions;
    forbidQuizSubmission.value = false;
}
function reloadPage() {
    window.location.reload();
}
</script>

<style scoped>
@reference '@/assets/styles/default.css';
button.collapse-btn {
    @apply px-xs py-xs absolute top-3 rounded-sm bg-(--overlay-surface) lg:top-12;

    box-shadow:
        0 4px 6px rgba(0, 0, 0, 0.1),
        0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>
